/**
 * http-client — ส่วนที่ **ทุก adapter ที่คุยกับ API ของเราทำเหมือนกัน**
 * (ประกอบ URL · จับ network error · แกะ error envelope · แปลง status เป็น `reason`)
 *
 * 🔴 **ตัวนี้ไม่ตัดสิน "นโยบายของ endpoint" ให้ใครทั้งสิ้น** — ไม่มีค่า default ของ
 *    `credentials` · ไม่มี `withAuth` flag · ไม่แอบตั้ง `content-type` ให้
 *    ผู้เรียกต้องบอกทุกครั้งว่าจะแนบอะไร เพราะ **นโยบายเป็นความรู้ของ endpoint
 *    ไม่ใช่ของท่อ** · เหตุผลที่ต้องเขียนกำกับไว้ (บทเรียนจริงของ repo นี้):
 *    · endpoint สาธารณะที่เผลอแนบ token = ลิงก์ที่แชร์ให้คนนอกเปิดไม่ได้ **เงียบ ๆ**
 *    · endpoint auth ที่ลืม `credentials: 'include'` = "login ได้แต่ refresh ไม่ได้"
 *      ซึ่งโผล่อีกที 15 นาทีต่อมา
 *    ⇒ ถ้าวันหนึ่งมีคนอยากใส่ default ให้สะดวก **นั่นคือจังหวะที่ความผิดพลาด 2 แบบนี้
 *      กลับมา** — ตัวที่กันไม่ให้เผลอคือ adapter ฝั่ง member ที่แยกฟังก์ชันต่อ นโยบาย
 *
 * ⚠️ **ไฟล์นี้ห้ามรู้จัก feature ไหนเลย** — `reason` เป็น `string` ธรรมดา ไม่ใช่ union
 *    ของฟีเจอร์ใด · ฝั่งที่รู้จักค่าคงที่ (`ACTIVITY_NOT_FOUND` · `REGISTER_*`) คือ
 *    adapter ของ scope นั้น ซึ่งเป็นคนแปลต่อ (กฎ `no-cross-scope-within-layer`
 *    บังคับข้อนี้อยู่แล้ว — lib ใน `shared/` import `member/` ไม่ได้)
 */

/** `reason` ที่ **ไม่ได้มาจาก backend** — ยิงไม่ออกเลย (เน็ตหลุด · DNS · CORS) */
export const NETWORK_ERROR = 'network_error';

/**
 * รูป error ที่ทุก endpoint ของเราตอบเหมือนกัน (`http-adapter-kit` ฝั่ง backend)
 * — `reason` คือค่าที่ FE พึ่งได้ · `rule` มีเฉพาะ error ที่มาจากกฎธุรกิจ
 */
export type HttpError = {
  reason: string;
  message: string;
  rule?: string;
  /**
   * HTTP status ที่ได้จริง · `undefined` = **ยิงไม่ออกเลย** (`NETWORK_ERROR`)
   *
   * 🔴 **มีไว้เพราะ `reason` อย่างเดียวตอบไม่ได้ทุกคำถาม** — backend ตอบ 401 ได้
   *    หลายทางโดยส่ง `reason` มาไม่เหมือนกัน (บางทีไม่ส่งเลย) แต่สำหรับผู้ใช้
   *    ทุกทางแปลว่า *"ต้อง login ใหม่"* ⇒ adapter ที่ต้องรวบเคสแบบนี้ต้องเห็น status
   *    · ถ้าไม่มีช่องนี้ adapter จะไปเดาจากสตริง `http_401` ซึ่งพังทันทีที่ backend
   *      เริ่มส่ง `reason` มาด้วย
   */
  status?: number;
};

export type HttpResult<T> = { ok: true; data: T } | { ok: false; error: HttpError };

export type HttpRequest = {
  method: 'GET' | 'POST';
  path: string;
  /**
   * ⚠️ **ไม่ส่ง = ไม่มี body และไม่มี `content-type`** ซึ่งต่างจาก "ส่ง body ว่าง"
   *    Fastify ตอบ 400 (`FST_ERR_CTP_EMPTY_JSON_BODY`) ทันทีที่ client ประกาศ
   *    `content-type: application/json` แล้วไม่ส่ง body — เจอจริงตอน Day 5
   */
  body?: unknown;
  headers?: Record<string, string>;
  /**
   * `'include'` เฉพาะ endpoint ที่ต้องใช้ refresh cookie (httpOnly บน `.theeliteinc.co`)
   * — **ไม่ใส่ให้เอง** เพราะ endpoint สาธารณะต้องไม่แนบอะไรเลย
   */
  credentials?: RequestCredentials;
  /** ข้อความที่ผู้ใช้เห็นเมื่อ backend ไม่ได้ส่ง `error.message` มา */
  fallbackMessage: string;
};

export type HttpClient = {
  request<T>(req: HttpRequest): Promise<HttpResult<T>>;
};

export function createHttpClient(baseUrl: string): HttpClient {
  return {
    async request<T>(req: HttpRequest): Promise<HttpResult<T>> {
      let res: Response;
      try {
        res = await fetch(`${baseUrl}${req.path}`, {
          method: req.method,
          ...(req.headers === undefined ? {} : { headers: req.headers }),
          ...(req.body === undefined ? {} : { body: JSON.stringify(req.body) }),
          ...(req.credentials === undefined ? {} : { credentials: req.credentials }),
        });
      } catch {
        // เน็ตมือถือหลุดกลางทาง = เรื่องปกติของกลุ่มผู้ใช้เรา ⇒ ต้องมีข้อความที่อ่านรู้เรื่อง
        return {
          ok: false,
          error: { reason: NETWORK_ERROR, message: 'เชื่อมต่อไม่ได้ ลองอีกครั้ง' },
        };
      }

      const payload: unknown = await res.json().catch(() => undefined);

      if (!res.ok) {
        const envelope = payload as
          | { error?: { reason?: string; message?: string; rule?: string } }
          | undefined;
        return {
          ok: false,
          error: {
            /**
             * `reason` จาก backend เป็น **enum ที่ FE พึ่งได้** (`http-adapter-kit`)
             * ⇒ ใช้ตัวนั้นก่อนเสมอ · fallback `http_<status>` เฉพาะตอนไม่มี envelope
             * เช่น **404 จาก endpoint ที่ยังไม่ถูก register** (Google/email ปิดที่ server)
             */
            reason: envelope?.error?.reason ?? `http_${res.status}`,
            message: envelope?.error?.message ?? req.fallbackMessage,
            status: res.status,
            ...(envelope?.error?.rule === undefined ? {} : { rule: envelope.error.rule }),
          },
        };
      }

      return { ok: true, data: payload as T };
    },
  };
}
